import type { HistoryItem } from "@/services/historyService";
import type { UserGoalsRecord } from "@/services/userGoalService";
import { getBestSkillResult } from "./progressUtils";

export interface StudyRecommendation {
  id: string;
  title: string;
  description: string;
  priority: "high" | "medium" | "low";
  actionLabel: string;
  actionPath: string;
}

interface GenerateParams {
  history: HistoryItem[];
  errors: any[];
  words: any[];
  flashcards: any[];
  goals?: UserGoalsRecord;
  flashcardStats?: { dueToday: number; total: number; newCards: number; memorized: number };
  t?: (key: string, options?: any) => string;
}

export function generateStudyRecommendations({
  history = [],
  errors = [],
  flashcards = [],
  goals,
  flashcardStats,
  t
}: GenerateParams): StudyRecommendation[] {
  const recs: StudyRecommendation[] = [];

  // 1. Pending Flashcards
  const pendingCount = flashcardStats?.dueToday ?? flashcards.filter(c => !c.nextReviewAt || new Date(c.nextReviewAt) <= new Date()).length;
  if (pendingCount > 0) {
    recs.push({
      id: "flashcards-pending",
      title: t ? t("dashboard.recommendations.flashcardsPending") : "Revisar flashcards pendentes",
      description: t 
        ? t("dashboard.recommendations.flashcardsPendingDesc", { count: pendingCount }) 
        : `Você tem ${pendingCount} flashcard${pendingCount > 1 ? "s" : ""} aguardando revisão pelo SRS hoje.`,
      priority: "high",
      actionLabel: t ? t("dashboard.recommendations.studyNow") : "Estudar agora",
      actionPath: "/flashcards/study?mode=due"
    });
  }

  // Evaluate skills vs goals
  const targetCe = goals?.targetCeNclc ?? 7;
  const targetCo = goals?.targetCoNclc ?? 7;
  const targetEe = goals?.targetEeNclc ?? 7;

  const bestCe = getBestSkillResult(history, "ce")?.nclc ?? 0;
  const bestCo = getBestSkillResult(history, "co")?.nclc ?? 0;
  const bestEe = getBestSkillResult(history, "ee")?.nclc ?? 0;

  if (history.length === 0) {
    recs.push({
      id: "first-sim",
      title: t ? t("dashboard.recommendations.firstSim") : "Faça seu primeiro simulado",
      description: t ? t("dashboard.recommendations.firstSimDesc") : "Complete um teste de diagnóstico para estabelecer suas métricas de base no sistema.",
      priority: "high",
      actionLabel: t ? t("dashboard.recommendations.startTest") : "Iniciar teste",
      actionPath: "/start-test"
    });
  } else {
    // 2. CO below target
    if (bestCo > 0 && bestCo < targetCo) {
      recs.push({
        id: "co-practice",
        title: t ? t("dashboard.recommendations.coPractice") : "Praticar Compréhension Orale",
        description: t 
          ? t("dashboard.recommendations.coPracticeDesc", { best: bestCo, target: targetCo }) 
          : `Seu melhor resultado em CO (NCLC ${bestCo}) está abaixo da sua meta definida de NCLC ${targetCo}.`,
        priority: "high",
        actionLabel: t ? t("dashboard.recommendations.coPracticeTest") : "Fazer teste de CO",
        actionPath: "/start-test"
      });
    }

    // 3. CE below target
    if (bestCe > 0 && bestCe < targetCe) {
      recs.push({
        id: "ce-practice",
        title: t ? t("dashboard.recommendations.cePractice") : "Praticar Compréhension Écrite",
        description: t 
          ? t("dashboard.recommendations.cePracticeDesc", { best: bestCe, target: targetCe }) 
          : `Seu melhor resultado em CE (NCLC ${bestCe}) está abaixo da sua meta definida de NCLC ${targetCe}.`,
        priority: "high",
        actionLabel: t ? t("dashboard.recommendations.cePracticeTest") : "Fazer teste de CE",
        actionPath: "/start-test"
      });
    }

    // 4. EE practice
    if (bestEe < targetEe) {
      recs.push({
        id: "ee-practice",
        title: t ? t("dashboard.recommendations.eePractice") : "Fazer uma produção escrita",
        description: t ? t("dashboard.recommendations.eePracticeDesc") : "Pratique a expressão escrita com temas reais do exame para ser corrigido pela IA ou professor.",
        priority: bestEe === 0 ? "high" : "medium",
        actionLabel: t ? t("dashboard.recommendations.eeTrain") : "Treinar EE",
        actionPath: "/start-test"
      });
    }
  }

  // 5. Writing Errors review
  if (errors.length > 0) {
    recs.push({
      id: "errors-review",
      title: t ? t("dashboard.recommendations.errorsReview") : "Revisar erros recorrentes",
      description: t 
        ? t("dashboard.recommendations.errorsReviewDesc", { count: errors.length }) 
        : `Você possui ${errors.length} erro${errors.length > 1 ? "s" : ""} cadastrado${errors.length > 1 ? "s" : ""} no seu banco de erros.`,
      priority: "medium",
      actionLabel: t ? t("dashboard.recommendations.viewErrorBank") : "Ver Banco de Erros",
      actionPath: "/writing-errors"
    });
  }

  const mainFocus = goals?.metadata?.mainFocus;
  if (mainFocus === "ce") {
    const ceRec = recs.find(r => r.id === "ce-practice");
    if (ceRec) {
      ceRec.priority = "high";
      ceRec.actionPath = "/start-test?competence=reading";
    } else {
      recs.push({
        id: "ce-practice",
        title: t ? t("dashboard.recommendations.cePracticeFocus") : "Praticar Compréhension Écrite (Foco)",
        description: t ? t("dashboard.recommendations.cePracticeFocusDesc") : "Você definiu CE como seu foco principal. Continue praticando simulados de leitura.",
        priority: "high",
        actionLabel: t ? t("dashboard.recommendations.cePracticeTest") : "Fazer teste de CE",
        actionPath: "/start-test?competence=reading"
      });
    }
  } else if (mainFocus === "co") {
    const coRec = recs.find(r => r.id === "co-practice");
    if (coRec) {
      coRec.priority = "high";
      coRec.actionPath = "/start-test?competence=listening";
    } else {
      recs.push({
        id: "co-practice",
        title: t ? t("dashboard.recommendations.coPracticeFocus") : "Praticar Compréhension Orale (Foco)",
        description: t ? t("dashboard.recommendations.coPracticeFocusDesc") : "Você definiu CO como seu foco principal. Continue praticando escuta e áudios.",
        priority: "high",
        actionLabel: t ? t("dashboard.recommendations.coPracticeTest") : "Fazer teste de CO",
        actionPath: "/start-test?competence=listening"
      });
    }
  } else if (mainFocus === "ee") {
    const eeRec = recs.find(r => r.id === "ee-practice");
    if (eeRec) {
      eeRec.priority = "high";
      eeRec.actionPath = "/start-test?competence=writing";
    } else {
      recs.push({
        id: "ee-practice",
        title: t ? t("dashboard.recommendations.eePracticeFocus") : "Praticar Expression Écrite (Foco)",
        description: t ? t("dashboard.recommendations.eePracticeFocusDesc") : "Você definiu EE como seu foco principal. Treine a escrita de redações.",
        priority: "high",
        actionLabel: t ? t("dashboard.recommendations.eeTrain") : "Treinar EE",
        actionPath: "/start-test?competence=writing"
      });
    }
  }

  // Sort: high first, then medium, then low
  const priorityWeight = { high: 1, medium: 2, low: 3 };
  recs.sort((a, b) => {
    if (a.priority !== b.priority) {
      return priorityWeight[a.priority] - priorityWeight[b.priority];
    }
    // if both same priority, boost the one matching mainFocus
    if (mainFocus === "ce" && a.id === "ce-practice") return -1;
    if (mainFocus === "ce" && b.id === "ce-practice") return 1;
    if (mainFocus === "co" && a.id === "co-practice") return -1;
    if (mainFocus === "co" && b.id === "co-practice") return 1;
    if (mainFocus === "ee" && a.id === "ee-practice") return -1;
    if (mainFocus === "ee" && b.id === "ee-practice") return 1;
    return 0;
  });

  return recs.slice(0, 4);
}

export function generateStudyPlan(recs: StudyRecommendation[], t?: (key: string, options?: any) => string): { title: string; actionPath?: string }[] {
  if (recs.length === 0) {
    return [
      { title: t ? t("dashboard.recommendations.studyPlanCE") : "Fazer um teste diagnóstico de CE", actionPath: "/start-test" },
      { title: t ? t("dashboard.recommendations.studyPlanCO") : "Fazer um teste diagnóstico de CO", actionPath: "/start-test" },
      { title: t ? t("dashboard.recommendations.studyPlanEE") : "Escrever uma produção EE", actionPath: "/start-test" },
      { title: t ? t("dashboard.recommendations.studyPlanFlashcards") : "Criar flashcards com erros encontrados", actionPath: "/flashcards" }
    ];
  }

  return recs.map(r => ({
    title: r.title,
    actionPath: r.actionPath
  }));
}
